import React, { useEffect, useState } from 'react';
import { Clock, Loader2, CalendarClock } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
// Re-cableado a lealify-base (/analytics/heatmap) — reemplaza Elasticsearch.
import { analyticsApi } from '../../../api/analyticsApi';
import type { VisitHeatmap } from '../../../api/analyticsApi';

// dow ISO: 1=Lunes ... 7=Domingo  → índice 0..6
const DOW = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

/**
 * Heatmap de visitas hora-local × día de semana (Fase 2 analítica).
 * Muestra de un vistazo las horas pico del negocio (útil para staffing
 * y promos de hora valle). Usa la zona horaria del negocio.
 */
const VisitsHeatmapPanel: React.FC = () => {
  const { currentUser } = useAuth();
  const [data, setData] = useState<VisitHeatmap | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!currentUser?.businessId) return;
      setLoading(true);
      try {
        const d = await analyticsApi.heatmap(currentUser?.timeZone || undefined);
        if (alive) setData(d);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [currentUser?.businessId, currentUser?.timeZone]);

  if (loading) {
    return (
      <div className="bg-surface shadow rounded-lg p-6 border border-line flex items-center justify-center min-h-[200px]">
        <Loader2 className="animate-spin h-5 w-5 text-blue-500 mr-3" />
        <span className="text-content-3 text-sm">Calculando horas pico...</span>
      </div>
    );
  }

  if (!data || data.totalVisits === 0) {
    return (
      <div className="bg-surface shadow rounded-lg p-6 border border-line">
        <h3 className="text-sm font-medium text-content-2 mb-3 flex items-center">
          <CalendarClock className="w-4 h-4 mr-2 text-blue-500" /> Horas pico
        </h3>
        <div className="flex flex-col items-center justify-center text-center py-8 text-content-3">
          <Clock className="h-10 w-10 mb-2 opacity-50" />
          <p className="text-sm">Aún no hay suficientes visitas para mostrar tus horas pico.</p>
        </div>
      </div>
    );
  }

  // Construir matriz [7 días][24 horas]
  const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
  data.cells.forEach((c) => {
    if (c.dow >= 1 && c.dow <= 7 && c.hour >= 0 && c.hour <= 23) grid[c.dow - 1][c.hour] = c.count;
  });

  // Rango de horas con actividad (para no dibujar 24 columnas vacías)
  let hMin = 23, hMax = 0;
  data.cells.forEach((c) => { if (c.count > 0) { if (c.hour < hMin) hMin = c.hour; if (c.hour > hMax) hMax = c.hour; } });
  if (hMin > hMax) { hMin = 8; hMax = 20; }
  const hours: number[] = [];
  for (let h = hMin; h <= hMax; h++) hours.push(h);

  // Hora pico (para el resumen textual)
  const peak = data.cells.reduce<{ dow: number; hour: number; count: number } | null>(
    (acc, c) => (c.count > (acc?.count || 0) ? c : acc), null
  );

  return (
    <div className="bg-surface shadow rounded-lg p-6 border border-line">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-medium text-content-2 flex items-center">
          <CalendarClock className="w-4 h-4 mr-2 text-blue-500" /> Horas pico
        </h3>
        {peak && (
          <span className="text-xs text-content-3">
            Pico: <b className="text-content-2">{DOW[peak.dow - 1]} {peak.hour}:00</b>
          </span>
        )}
      </div>
      <p className="text-[11px] text-content-3 mb-3">Visitas por hora y día. Celda más intensa = más visitas.</p>

      <div className="w-full grid gap-1.5"
        style={{ gridTemplateColumns: `40px repeat(${hours.length}, minmax(0, 1fr))` }}>
        {/* Fila de encabezado de horas */}
        <div />
        {hours.map((h) => (
          <div key={`h-${h}`} className="text-[10px] text-content-3 text-center leading-none pb-1">{h}h</div>
        ))}
        {/* Una fila por día de semana */}
        {DOW.map((label, di) => (
          <React.Fragment key={`row-${di}`}>
            <div className="text-xs text-content-3 font-medium flex items-center pr-1">{label}</div>
            {hours.map((h) => {
              const v = grid[di][h];
              const intensity = data.maxCount > 0 ? v / data.maxCount : 0;
              const bg = v === 0 ? 'var(--c-surface-2)' : `rgba(96,165,250,${(0.18 + intensity * 0.82).toFixed(2)})`;
              return (
                <div key={`c-${di}-${h}`}
                  title={`${label} ${h}:00 — ${v} ${v === 1 ? 'visita' : 'visitas'}`}
                  className="rounded"
                  style={{ background: bg, height: 30 }} />
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default VisitsHeatmapPanel;
